import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import axios from "axios";
import { toast } from "@/hooks/use-toast";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function isUndefinedOrNull(value: unknown) {
  return value === undefined || value === null;
}

export function isEmpty(value: unknown) {
  return value === "" || isUndefinedOrNull(value);
}

// Helper function to format dates
export const formatDate = (date: Date | null) => {
  if (!date) return;
  const dateObj = new Date(date);
  return `${(dateObj.getMonth() + 1)
    .toString()
    .padStart(2, "0")}/${dateObj.getDate()}/${dateObj.getFullYear()}`;
};

export const formatTime = (date: Date | null) => {
  if (!date) return;
  const dateObj = new Date(date);
  return `${dateObj.getHours() % 12 || 12}:${dateObj
    .getMinutes()
    .toString()
    .padStart(2, "0")}${dateObj.getHours() >= 12 ? "PM" : "AM"}`;
};

export const formatDateTime = (date: Date | null) => {
  if (!date) return;
  const dateObj = new Date(date);
  return `${formatDate(dateObj)} @ ${formatTime(dateObj)}`;
};

// Helper functions to centralize success/error from server response
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as Record<string, unknown> | undefined;
    const detail = data?.detail;
    const detailStr =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(String).join(", ")
          : undefined;
    return (
      (typeof data?.error === "string" && data.error) ||
      (typeof data?.message === "string" && data.message) ||
      detailStr ||
      error.message ||
      "Network error"
    );
  }
  return error instanceof Error ? error.message : "Unknown error occurred";
};

export const handleMutationError = (error: unknown, customMessage?: string) => {
  toast({
    title: "Error",
    description: customMessage || getErrorMessage(error),
    variant: "destructive",
  });
};

export const handleMutationSuccess = (message: string) => {
  toast({
    title: "Success",
    description: message || "Operation completed successfully",
    variant: "default",
  });
};

// Generic utility function to convert enum + labels to options

export interface Option<T = string> {
  label: string;
  value: T;
}

export function enumToOptions<T extends Record<string, string | number>>(
  enumObj: T,
  labels: Record<T[keyof T], string>
): Option<T[keyof T]>[] {
  return Object.values(enumObj).map((value) => ({
    label: labels[value as T[keyof T]],
    value: value as T[keyof T],
  }));
}

export * from "./mapper";
export * from "./app";
